import React from 'react';

interface CitationModalProps {
  type: 'pdf' | 'text';
  visible: boolean;
  /** Shown while the PDF blob is still downloading (streaming API). */
  loading?: boolean;
  pdfUrl?: string | null;
  pdfPage?: number;
  title?: string;
  textContent?: string;
  onClose: () => void;
}

const CitationModal: React.FC<CitationModalProps> = ({
  type,
  visible,
  loading = false,
  pdfUrl,
  pdfPage = 1,
  title,
  textContent,
  onClose,
}) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-[90vw] h-[85vh] max-w-5xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-5 py-3 border-b border-gray-200 rounded-t-xl">
          <h3 className="text-sm font-semibold text-gray-800">
            {title || (type === 'pdf' ? 'Citation PDF' : 'Citation Text')}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden relative">
          {type === 'pdf' && loading && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white z-10"
              role="status"
              aria-live="polite"
              aria-label="Loading PDF"
            >
              <svg
                className="w-5 h-5 animate-spin text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-sm text-gray-500">Loading PDF…</p>
            </div>
          )}
          {type === 'pdf' && pdfUrl && !loading && (
            <iframe
              src={`${pdfUrl}#page=${pdfPage}`}
              title="Citation PDF"
              className="w-full h-full border-0 rounded-b-xl"
            />
          )}
          {type === 'text' && (
            <div className="p-6 overflow-y-auto h-full">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed font-sans">
                {textContent}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CitationModal;
